import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { LicensesCollectionModel } from '../../models/LicensesModel'

import './ResellerModal.scss'
import { cn } from '@bem-react/classname'
import { useTranslation } from 'react-i18next'
import { Select } from '@/components/form/Select'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { resellersSelector } from '@/modules/AccountPage/store/resellers.selector'
import { getResellersThunk } from '@/modules/AccountPage/store/resellers.slice'
import useBackendError from '@/hooks/useBackendError'
import { Button } from '@/components/Button'
import LicensesService from '../../LicensesService'
import { setToast } from '@/core/services/Toast/store/toast.slice'
import { ToastType } from '@/core/services/Toast/types/ToastType'
import { MAX_PAGE_SIZE } from '@/config/constants'

const cnResellerModal = cn('ResellerModal')

interface ResellerModalProps {
  onChange: () => void
  onCancel: () => void
  license: LicensesCollectionModel | null
}

const ResellerModal: FC<ResellerModalProps> = ({
  onChange,
  onCancel,
  license,
}: ResellerModalProps) => {
  const { t } = useTranslation()
  const resellers = useAppSelector(resellersSelector)
  const dispatch = useAppDispatch()
  const [setBackendError] = useBackendError()
  const [currentResellerId, setCurrentResellerId] = useState<
    string | undefined
  >()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const loadResellers = useCallback(async () => {
    try {
      setIsLoading(true)

      await dispatch(
        getResellersThunk({
          join: 'version',
          order: 'desc',
          orderBy: 'ActivationDate',
          pageSize: MAX_PAGE_SIZE,
        }),
      ).unwrap()
    } catch (errors) {
      setBackendError(errors)
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, setBackendError, setIsLoading])

  useEffect(() => {
    loadResellers()
  }, [])

  useEffect(() => {
    setCurrentResellerId(license?.resellerId)
  }, [license?.resellerId])

  const resellersOptions = useMemo(() => {
    return (
      resellers?.collection.map(item => {
        const isSameIdAndLabel =
          String(item.name).toLowerCase() === String(item.id).toLowerCase()

        return {
          value: item.id,
          label: isSameIdAndLabel ? item.name : `${item.name} (${item.id})`,
        }
      }) || []
    )
  }, [resellers?.collection])

  const handleResellerChange = async (
    value: string | number | string[] | number[],
  ) => {
    const newResellerId = value as string

    setCurrentResellerId(newResellerId)
  }

  const handleSave = async () => {
    if (!license?.id || !currentResellerId) {
      return
    }

    try {
      setIsSubmitting(true)
      await LicensesService.licensesReassignCustomReseller({
        licenseId: license.id,
        resellerId: currentResellerId,
      })

      dispatch(
        setToast({
          type: ToastType.success,
          message: t('Reseller successfully assigned'),
        }),
      )

      onChange()
    } catch (e) {
      setBackendError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={cnResellerModal()}>
      <div className={cnResellerModal('select')}>
        <Select
          name='resellerId'
          className={cnResellerModal('list')}
          label={t('Resellers list')}
          placeholder={t('Select reseller')}
          options={resellersOptions}
          optionLabel='label'
          optionValue='value'
          onChange={handleResellerChange}
          isLoading={isLoading}
          disabled={isSubmitting}
          defaultValue={currentResellerId}
        />
      </div>
      <div className={cnResellerModal('buttons')}>
        <Button
          large
          color='outline-gray'
          disabled={isSubmitting}
          className={cnResellerModal('button')}
          onClick={onCancel}
        >
          {t('Cancel')}
        </Button>

        <Button
          large
          color='blue'
          isLoading={isSubmitting}
          className={cnResellerModal('button')}
          onClick={handleSave}
        >
          {t('Save')}
        </Button>
      </div>
    </div>
  )
}

export default ResellerModal
